import { useQuery } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { Brand } from "@features/events/types";

type UseMyBrandResult = {
  myBrand: Brand | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  refetchMyBrand: () => void;
};

/**
 * Hook to fetch the current company's brand (my brand)
 * Uses the /e/me endpoint to get the brand representation of the current event organizer
 * 
 * @param eventOrganizerId - The healthcare company ID (event organizer)
 * @param enabled - Whether the query should be enabled (default: true)
 * @returns Query result with the brand data
 */
export const useMyBrand = (
  eventOrganizerId?: string,
  enabled = true
): UseMyBrandResult => {
  const { GET } = useApiRequest();

  const {
    data: myBrand,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<Brand>({
    queryKey: ["my-brand", eventOrganizerId],
    queryFn: () => GET<Brand>(`/api/healthcare-companies/${eventOrganizerId}/e/me`),
    enabled: enabled && Boolean(eventOrganizerId),
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });

  return {
    myBrand,
    isLoading,
    isFetching,
    isError,
    error: error ?? null,
    refetchMyBrand: refetch,
  } satisfies UseMyBrandResult;
};


